<script setup lang="ts">
  import BoldFontIcon from '@/shared/assets/icons/BoldFontIcon.svg';
  import ItalicFontIcon from '@/shared/assets/icons/ItalicFontIcon.svg';
  import StrikethroughFontIcon from '@/shared/assets/icons/StrikethroughFontIcon.svg';
  import UnderscoreFontIcon from '@/shared/assets/icons/UnderscoreFontIcon.svg';
  import TextAlignLeftIcon from '@/shared/assets/icons/TextAlignLeftIcon.svg';
  import TextAlignCenterIcon from '@/shared/assets/icons/TextAlignCenterIcon.svg';
  import TextAlignRightIcon from '@/shared/assets/icons/TextAlignRightIcon.svg';
  import {
    FormControl,
    FormField,
    FormItem,
    FormMessage,
    IconImage,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
  } from '@/shared/ui';
  import { computed, toRef } from 'vue';
  import * as z from 'zod';
  import { DEFAULT_COLORS, DEFAULT_FONT, DEFAULT_SPACING } from '../../../lib/element-defaults';
  import type { ElementProperties } from '../../../model/form.types';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import { useTextFormatToggles } from '../../../model/useTextFormatToggles';
  import MarginControl from './MarginControl.vue';
  import SliderField from './SliderField.vue';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  type FormValues = {
    width?: number;
    paddingUniform?: number;
    marginTop?: number;
    marginRight?: number;
    marginBottom?: number;
    marginLeft?: number;
    color?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'semibold' | 'bold';
    fontStyle?: 'normal' | 'italic';
    textDecoration?: 'none' | 'underline' | 'line-through';
    textAlign?: 'left' | 'center' | 'right';
  };

  const { values, setValues } = usePropertyForm<FormValues, ElementProperties>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      width: z.number().min(10).max(100).optional(),
      paddingUniform: z.number().min(0).max(100).optional(),
      marginTop: z.number().min(0).max(100).optional(),
      marginRight: z.number().min(0).max(100).optional(),
      marginBottom: z.number().min(0).max(100).optional(),
      marginLeft: z.number().min(0).max(100).optional(),
      color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida (use formato #RRGGBB)')
        .optional(),
      fontSize: z.number().min(8).max(72).optional(),
      fontWeight: z.enum(['normal', 'semibold', 'bold']).optional(),
      fontStyle: z.enum(['normal', 'italic']).optional(),
      textDecoration: z.enum(['none', 'underline', 'line-through']).optional(),
      textAlign: z.enum(['left', 'center', 'right']).optional(),
    }),
    extractInitialValues: (properties: ElementProperties | undefined): FormValues => ({
      width: properties?.width ?? 100,
      paddingUniform: properties?.padding?.top ?? DEFAULT_SPACING.padding.top,
      marginTop: properties?.margin?.top ?? DEFAULT_SPACING.margin.top,
      marginRight: properties?.margin?.right ?? DEFAULT_SPACING.margin.right,
      marginBottom: properties?.margin?.bottom ?? DEFAULT_SPACING.margin.bottom,
      marginLeft: properties?.margin?.left ?? DEFAULT_SPACING.margin.left,
      color: properties?.color ?? DEFAULT_COLORS.text,
      fontSize: properties?.fontSize ?? DEFAULT_FONT.size,
      fontWeight: properties?.fontWeight ?? DEFAULT_FONT.weight,
      fontStyle: properties?.fontStyle ?? DEFAULT_FONT.style,
      textDecoration: properties?.textDecoration ?? DEFAULT_FONT.decoration,
      textAlign: (properties?.textAlign as 'left' | 'center' | 'right') ?? 'left',
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: ElementProperties) => {
      const paddingValue = values.paddingUniform ?? DEFAULT_SPACING.padding.top;
      const nextMargin = {
        top: values.marginTop ?? 0,
        right: values.marginRight ?? 0,
        bottom: values.marginBottom ?? 0,
        left: values.marginLeft ?? 0,
      };
      const currentMargin = currentProperties.margin;
      const didMarginChange =
        currentMargin?.top !== nextMargin.top ||
        currentMargin?.right !== nextMargin.right ||
        currentMargin?.bottom !== nextMargin.bottom ||
        currentMargin?.left !== nextMargin.left;

      return {
        properties: {
          ...currentProperties,
          width: values.width !== undefined && values.width < 100 ? values.width : undefined,
          color: values.color,
          fontSize: values.fontSize,
          fontWeight: values.fontWeight,
          fontStyle: values.fontStyle,
          textDecoration: values.textDecoration,
          textAlign: values.textAlign ?? 'left',
          padding: {
            top: paddingValue,
            right: paddingValue,
            bottom: paddingValue,
            left: paddingValue,
          },
          margin: nextMargin,
          autoHeaderSpacing: didMarginChange ? undefined : currentProperties.autoHeaderSpacing,
        },
      };
    },
  });

  const marginValues = computed(() => ({
    top: values?.marginTop ?? 0,
    right: values?.marginRight ?? 0,
    bottom: values?.marginBottom ?? 0,
    left: values?.marginLeft ?? 0,
  }));

  function onMarginUpdate(margin: { top: number; right: number; bottom: number; left: number }) {
    setValues({
      marginTop: margin.top,
      marginRight: margin.right,
      marginBottom: margin.bottom,
      marginLeft: margin.left,
    });
  }

  const { toggleFontWeight, toggleFontStyle, toggleUnderline, toggleStrikethrough } =
    useTextFormatToggles({
      values: toRef(() => values),
      setValues,
    });

  const currentAlignIcon = computed(() => {
    const icons = {
      left: TextAlignLeftIcon,
      center: TextAlignCenterIcon,
      right: TextAlignRightIcon,
    };
    return icons[values?.textAlign ?? 'left'] ?? TextAlignLeftIcon;
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <SliderField
        label="Largura do<br />elemento"
        suffix="px"
        :min="10"
        :max="100"
        input-placeholder="100"
        :empty-fallback="100"
        :model-value="values?.width ?? 100"
        @update:model-value="(v) => setValues({ width: v })"
      />
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-2">
      <p class="text-sm font-semibold text-gray-11">
        Espaçamento
      </p>
      <SliderField
        label="Interno"
        suffix="px"
        :min="0"
        :max="100"
        input-placeholder="0"
        :empty-fallback="0"
        :model-value="values?.paddingUniform ?? 0"
        @update:model-value="(v) => setValues({ paddingUniform: v })"
      />
      <MarginControl
        :model-value="marginValues"
        :element-id="elementId"
        @update:model-value="onMarginUpdate"
      />
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-2">
      <p class="text-sm text-gray-11">
        Fonte Rótulo
      </p>

      <div class="bg-gray-02 flex gap-0.5 h-[42px] items-center p-[2px] rounded-[8px] w-full">
        <button
          type="button"
          :class="[
            'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
            values?.fontWeight === 'bold'
              ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
              : '',
          ]"
          title="Negrito"
          @click="toggleFontWeight"
        >
          <BoldFontIcon class="pointer-events-none size-6" />
        </button>
        <button
          type="button"
          :class="[
            'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
            values?.fontStyle === 'italic'
              ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
              : '',
          ]"
          title="Itálico"
          @click="toggleFontStyle"
        >
          <ItalicFontIcon class="pointer-events-none size-6" />
        </button>
        <button
          type="button"
          :class="[
            'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
            values?.textDecoration === 'underline'
              ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
              : '',
          ]"
          title="Sublinhado"
          @click="toggleUnderline"
        >
          <UnderscoreFontIcon class="pointer-events-none size-6" />
        </button>
        <button
          type="button"
          :class="[
            'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
            values?.textDecoration === 'line-through'
              ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
              : '',
          ]"
          title="Tachado"
          @click="toggleStrikethrough"
        >
          <StrikethroughFontIcon class="pointer-events-none size-6" />
        </button>
      </div>

      <div class="flex items-center gap-[10px]">
        <input
          type="number"
          min="8"
          max="72"
          placeholder="10"
          :value="values?.fontSize ?? 10"
          class="flex-1 h-[42px] bg-white-01 border border-gray-02 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] pl-3 pr-1 text-sm text-gray-07 outline-none min-w-0"
          @input="
            (e) =>
              setValues({
                fontSize: Number((e.target as HTMLInputElement).value),
              })
          "
          @blur="
            (e) => {
              const v = Number((e.target as HTMLInputElement).value);
              if (v < 8) setValues({ fontSize: 8 });
              else if (v > 72) setValues({ fontSize: 72 });
            }
          "
        >

        <div class="w-[68px]">
          <FormField
            v-slot="{ componentField }"
            name="textAlign"
          >
            <Select v-bind="componentField">
              <FormControl>
                <SelectTrigger class="h-11 w-full">
                  <IconImage
                    :src="currentAlignIcon"
                    alt="Alinhamento do texto"
                    class="size-5"
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent class="min-w-0">
                <SelectItem
                  value="left"
                  class="pr-2"
                >
                  <IconImage
                    :src="TextAlignLeftIcon"
                    alt="Esquerda"
                    class="size-5"
                  />
                </SelectItem>
                <SelectItem
                  value="center"
                  class="pr-2"
                >
                  <IconImage
                    :src="TextAlignCenterIcon"
                    alt="Centro"
                    class="size-5"
                  />
                </SelectItem>
                <SelectItem
                  value="right"
                  class="pr-2"
                >
                  <IconImage
                    :src="TextAlignRightIcon"
                    alt="Direita"
                    class="size-5"
                  />
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormField>
        </div>
      </div>

      <FormField
        v-slot="{ field, handleChange }"
        name="color"
      >
        <FormItem class="flex flex-col gap-2">
          <FormControl>
            <div class="flex items-center gap-2 h-[42px]">
              <div
                class="relative flex items-center justify-center size-8 shrink-0 cursor-pointer"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="pointer-events-none shrink-0"
                >
                  <path
                    d="M13.7498 12.5L10.4165 2.5H9.58317L6.24984 12.5M7.08317 10H12.9165"
                    stroke="var(--color-gray-07)"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M1.6665 16.6667H18.3332"
                    :stroke="(field.value as string) || '#000000'"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div
                class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
              >
                <div
                  class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
                  :style="{ backgroundColor: (field.value as string) || '#ffffff' }"
                >
                  <input
                    type="color"
                    :value="(field.value as string) || '#000000'"
                    class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    @input="(e) => handleChange((e.target as HTMLInputElement).value)"
                  >
                </div>
                <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
                <input
                  :value="((field.value as string) || '').replace('#', '')"
                  type="text"
                  class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
                  placeholder="000000"
                  @input="(e) => handleChange('#' + (e.target as HTMLInputElement).value)"
                >
              </div>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
